import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {GridColDef} from '@mui/x-data-grid';
import message from 'src/components/Message';
import {PageHeader, EntioDataGrid} from 'src/components/ui';
import {formatDate} from 'src/utils/common';
import {useOrgId} from 'src/hooks';

// Import real API types and services
import {
  CbwDevice,
  CbwDeviceRegistration,
  CbwDeviceRegistrationStatusResponse,
  CbwDeviceRegistrationsResponse,
  CbwDeviceDeleteResponse,
  CbwDeviceRelayLinksResponse, // Add this import
} from 'src/apis/autoGenerate/types/cbwDevice';
import {
  cbwDeviceServiceGetRegistrations,
  cbwDeviceServiceDelete,
  cbwDeviceServiceGetRelayLinks, // Add this import
} from 'src/apis/autoGenerate/services';

// Import the new modal component
import AddCbwDeviceModal from 'src/components/Modals/AddCBWDevice';
import {AcsProviderTabPanel} from './AcsProviderTabPanel';

// Interface for our UI representation of devices from registrations
interface IntegrationDevice {
  id: string;
  serial_number?: string;
  device_name?: string;
  online?: boolean;
  created_at?: string;
  last_seen_at?: string;
  firmware_version?: string;
  registration_status?: string;
  cbw_device_id?: string;
  // UI specific fields
  displayName?: string;
  statusLabel?: string;
  // Add linked intercom device info
  linkedIntercomDevice?: {
    id: string;
    name: string;
    relayName?: string;
    relayNumber?: number;
  } | null;
}

const INTEGRATION_TAB_KEYS = ['cbw', 'pdk'] as const;
type IntegrationTabKey = (typeof INTEGRATION_TAB_KEYS)[number];

function tabKeyToIndex(k: string): 0 | 1 {
  if (k === 'pdk') return 1;
  return 0;
}

function indexToTabKey(i: number): IntegrationTabKey {
  if (i === 1) return 'pdk';
  return 'cbw';
}

export default function IntegrationPage() {
  const {orgId} = useOrgId();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIndex = useMemo(() => {
    const raw = (searchParams.get('tab') || 'cbw').toLowerCase();
    if (INTEGRATION_TAB_KEYS.includes(raw as IntegrationTabKey)) {
      return tabKeyToIndex(raw);
    }
    return 0;
  }, [searchParams]);

  const setTabIndex = (index: number) => {
    setSearchParams({tab: indexToTabKey(index)}, {replace: true});
  };
  const [devices, setDevices] = useState<IntegrationDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDeviceModalOpen, setAddDeviceModalOpen] = useState(false);
  const [editDeviceModalOpen, setEditDeviceModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IntegrationDevice | null>(null);
  const [deviceActionAnchorEl, setDeviceActionAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Add state to store edit device data
  const [editDeviceData, setEditDeviceData] = useState<any>(undefined);

  // Add state for relay links
  const [deviceRelayLinks, setDeviceRelayLinks] = useState<{[deviceId: string]: any}>({});

  const deviceActionOpen = Boolean(deviceActionAnchorEl);
  const deviceActionId = deviceActionOpen ? 'device-action-popover' : undefined;

  // Load devices when component mounts
  useEffect(() => {
    if (orgId) {
      loadDevices();
    }
  }, [orgId]);

  // Function to load relay links for all devices
  const loadDeviceRelayLinks = async (devices: IntegrationDevice[]) => {
    try {
      const relayLinksMap: {[deviceId: string]: any} = {};

      // Fetch relay links for each device
      const promises = devices.map(async device => {
        if (device.id) {
          try {
            const response = await cbwDeviceServiceGetRelayLinks({
              deviceId: device.id,
            });
            if (response.data.success && response.data.data && response.data.data.length > 0) {
              // Store ALL active relay links instead of just the first one
              relayLinksMap[device.id] = response.data.data.filter(
                (link: any) => link.is_active !== false
              );
            }
          } catch (error) {
            console.warn(`Failed to load relay links for device ${device.id}:`, error);
          }
        }
      });

      await Promise.all(promises);
      setDeviceRelayLinks(relayLinksMap);
    } catch (error) {
      console.error('Failed to load device relay links:', error);
    }
  };

  // Update loadDevices to also load relay links
  const loadDevices = async () => {
    setLoading(true);
    try {
      // Use the real API to get CBW device registrations
      const response = await cbwDeviceServiceGetRegistrations({
        orgId: orgId,
        page: 1,
        limit: 100, // Get all devices for now
      });

      const registrationsData = response.data.data;
      if (registrationsData?.registrations) {
        // Transform registrations to our UI format
        const transformedDevices: IntegrationDevice[] = registrationsData.registrations.map(
          (registration: CbwDeviceRegistration) => {
            const device = registration.entrio_controlbyweb_devices;
            return {
              // Use entrio_controlbyweb_devices.id as the main ID for deletion
              id: device?.id || registration.id || '',
              serial_number: registration.serial_number || device?.serial_number,
              device_name: device?.device_name,
              online: device?.online,
              created_at: registration.created_at || device?.created_at,
              last_seen_at: device?.last_seen_at,
              firmware_version: device?.firmware_version,
              registration_status: registration.registration_status,
              cbw_device_id: registration.cbw_device_id || device?.cbw_device_id,
              displayName: device?.device_name || `Device ${registration.serial_number}`,
              statusLabel: getStatusLabelFromRegistration(registration),
              linkedIntercomDevice: null, // Will be populated by loadDeviceRelayLinks
            };
          }
        );
        setDevices(transformedDevices);

        // Load relay links after devices are loaded
        await loadDeviceRelayLinks(transformedDevices);
      } else {
        setDevices([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load devices:', error);
      message.error('Failed to load devices');
      setLoading(false);
    }
  };

  const getStatusLabelFromRegistration = (registration: CbwDeviceRegistration): string => {
    const device = registration.entrio_controlbyweb_devices;

    // For completed registrations, use the actual device online status
    if (registration.registration_status?.toLowerCase() === 'completed') {
      if (device?.online === true) return 'online';
      if (device?.online === false) return 'offline';
      return 'unknown';
    }

    // For non-completed registrations, show as pending
    return 'pending';
  };

  const deleteDevice = async (device: IntegrationDevice) => {
    // Use the entrio_controlbyweb_devices.id, not cbw_device_id
    const deviceId = device.id; // This should be the entrio_controlbyweb_devices.id

    if (!deviceId) {
      message.error('Device ID not found');
      return;
    }

    setLoading(true);
    try {
      // Use the real API to delete CBW device
      const response = await cbwDeviceServiceDelete({
        deviceId: deviceId, // Use entrio_controlbyweb_devices.id
      });

      if (response.data.success) {
        message.success('Device deleted successfully');
        // Reload the devices list
        await loadDevices();
      } else {
        message.error('Failed to delete device');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to delete device:', error);
      message.error('Failed to delete device');
      setLoading(false);
    }
  };

  const handleDeviceActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    device: IntegrationDevice
  ) => {
    setSelectedDevice(device);
    setDeviceActionAnchorEl(event.currentTarget);
  };

  const handleDeviceActionClose = () => {
    setDeviceActionAnchorEl(null);
    setSelectedDevice(null);
  };

  const handleEditDevice = () => {
    if (selectedDevice) {
      // Store the edit device data before opening modal
      const relayLinks = deviceRelayLinks[selectedDevice.id] || [];

      const editData = {
        deviceId: selectedDevice.id,
        serialNumber: selectedDevice.serial_number || '',
        deviceName: selectedDevice.device_name || '',
        existingRelayLinks: Array.isArray(relayLinks)
          ? relayLinks.map((link: any) => ({
              id: link.id,
              intercomDeviceId: link.entrio_intercom_devices?.id || '',
              intercomDeviceName: link.entrio_intercom_devices?.name || '',
              relayNumber: link.relay_number || 1,
              relayName: link.relay_name || '',
              triggerDurationMs: link.trigger_duration_ms || 5000,
            }))
          : [],
      };

      console.log('Edit device data prepared:', editData);

      setEditDeviceData(editData);
      setEditDeviceModalOpen(true);
      handleDeviceActionClose();
    }
  };

  const handleDeleteDevice = () => {
    if (selectedDevice) {
      deleteDevice(selectedDevice);
      handleDeviceActionClose();
    }
  };

  const getStatusColor = (statusLabel?: string) => {
    switch (statusLabel) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusDisplayText = (device: IntegrationDevice) => {
    // Use the actual device online status, not registration status
    if (device.online === true) return 'Online';
    if (device.online === false) return 'Offline';
    return 'Unknown';
  };

  const handleDeviceAdded = () => {
    // Reload devices when a new device is added or updated
    loadDevices();
  };

  const columns: GridColDef[] = [
    {
      field: 'serial_number',
      headerName: 'Serial Number',
      width: 150,
      flex: 0.8,
    },
    {
      field: 'device_name',
      headerName: 'Device Name',
      width: 180,
      flex: 1,
      renderCell: params => params.value || '-',
    },
    {
      field: 'online', // Change this to use the online field directly
      headerName: 'Status',
      width: 120,
      flex: 0.6,
      renderCell: params => (
        <Chip
          label={getStatusDisplayText(params.row)}
          color={getStatusColor(params.row.statusLabel) as any}
          size="small"
          sx={{textTransform: 'capitalize'}}
        />
      ),
    },
    // Add the new Linked Intercom Device column
    {
      field: 'linkedIntercom', // Change this to a unique field name
      headerName: 'Linked Intercom',
      width: 200,
      flex: 1.2,
      renderCell: params => {
        const relayLinks = deviceRelayLinks[params.row.id] || []; // Get all relay links
        if (!Array.isArray(relayLinks) || relayLinks.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not linked
            </Typography>
          );
        }

        // Show first link and count if there are more
        const firstLink = relayLinks[0];
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {firstLink.entrio_intercom_devices?.name || 'Unknown'}
            </Typography>
            {firstLink.relay_name && (
              <Typography variant="caption" color="text.secondary">
                Relay {firstLink.relay_number}: {firstLink.relay_name}
              </Typography>
            )}
            {relayLinks.length > 1 && (
              <Typography variant="caption" color="primary" sx={{display: 'block', mt: 0.5}}>
                +{relayLinks.length - 1} more link{relayLinks.length > 2 ? 's' : ''}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'firmware_version',
      headerName: 'Firmware',
      width: 120,
      flex: 0.6,
      renderCell: params => params.value || '-',
    },
    {
      field: 'last_seen_at',
      headerName: 'Last Seen',
      width: 160,
      flex: 1,
      renderCell: params => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'created_at',
      headerName: 'Added Date',
      width: 160,
      flex: 1,
      renderCell: params => formatDate(params.value),
    },
    {
      field: 'actions', // Change this to a unique field name
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <IconButton onClick={e => handleDeviceActionClick(e, params.row)}>
          <MoreHorizIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Integrations"
        subtitle="Add hardware or cloud access: Control by Web relays or PDK. Use the tab for each system."
        actions={
          tabIndex === 0 ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={loadDevices}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={() => setAddDeviceModalOpen(true)}
                startIcon={<AddIcon />}
              >
                Add device
              </Button>
            </Stack>
          ) : null
        }
      />

      <Tabs
        value={tabIndex}
        onChange={(_e, v) => setTabIndex(v)}
        sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}
        aria-label="integration provider tabs"
      >
        <Tab label="Control by Web" id="integration-tab-cbw" />
        <Tab label="PDK" id="integration-tab-pdk" />
      </Tabs>

      {tabIndex === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Control by Web
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Register control boards and link relays to intercoms. PDK is on its own tab.
            </Typography>
            <Box mt={2}>
              <EntioDataGrid
                loading={loading}
                rows={devices}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                emptyMessage="No devices found"
                emptyDescription="Add your first Control by Web device to get started"
                sx={{minWidth: 700}}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {tabIndex === 1 && <AcsProviderTabPanel provider="PDK" />}

      {/* Add Device Modal */}
      <AddCbwDeviceModal
        open={addDeviceModalOpen}
        onClose={() => setAddDeviceModalOpen(false)}
        onDeviceAdded={handleDeviceAdded}
        orgId={orgId}
      />

      {/* Edit Device Modal */}
      <AddCbwDeviceModal
        open={editDeviceModalOpen}
        onClose={() => {
          setEditDeviceModalOpen(false);
          setEditDeviceData(undefined); // Clear edit data when closing
        }}
        onDeviceAdded={handleDeviceAdded}
        orgId={orgId}
        mode="edit"
        editDeviceData={editDeviceData}
      />

      {/* Device Actions Popover */}
      <Popover
        id={deviceActionId}
        open={deviceActionOpen}
        anchorEl={deviceActionAnchorEl}
        onClose={handleDeviceActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleEditDevice}>
              <EditIcon sx={{mr: 1, fontSize: '1.2rem'}} />
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleDeleteDevice}>
              <DeleteIcon sx={{mr: 1, fontSize: '1.2rem'}} />
              <ListItemText primary="Delete" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </Box>
  );
}
